import { formatDateish, formatPrice as formatSharedPrice } from "@osmix/formatters";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// shadcn's class-name composer — file lives at src/lib/utils.ts because
// shadcn's components.json points its `utils` alias at @/lib/utils.
// Other small cross-cutting helpers (sleep, formatters, …) get added
// here so the app has one place to look for utilities.
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// Awaitable delay. Used by mock api implementations to add realistic
// latency so loading-state UX stays honest during dev.
export const sleep = (ms: number): Promise<void> =>
	new Promise((resolve) => setTimeout(resolve, ms));

// RUB price via the shared @osmix/formatters formatter: ru-RU grouping, no
// fraction digits, rendered "<amount> ₽". The single place admin formats money.
export const formatPrice = (n: number): string => formatSharedPrice(n);

export const formatDateTime = (iso: string): string =>
	formatDateish(iso, { dateStyle: "medium", timeStyle: "short" });

const BUSINESS_FORM_CODES = ["legal_entity", "self_employed", "individual_entrepreneur"];

/**
 * Код формы бизнеса из анкеты («individual_entrepreneur») → подпись («ИП»).
 * Подписи из реквизитов («ИП без НДС») и пустые значения — как есть.
 */
export function businessFormLabel(
	t: (key: string) => string,
	value: string | null | undefined,
): string | undefined {
	const code = value?.trim();
	if (!code || !BUSINESS_FORM_CODES.includes(code)) return value ?? undefined;
	return t(`sellers.businessForms.${code}`);
}

/**
 * Ответы анкеты продавца хранятся кодами вариантов кабинета (seller-web
 * survey): «manufacturer», «womenClothing», «marketplaces». В карточке продавца
 * они печатались как есть. Известные коды — словами, как их видел продавец;
 * неизвестные (свой вариант, старые записи) — как ввёл продавец.
 */
const SURVEY_LABELS: Record<"role" | "category" | "channel" | "contact", Record<string, string>> = {
	role: {
		manufacturer: "Производитель товаров",
		dealer: "Официальный дилер или дистрибьютор",
		contract: "Продаю по договору с производителями",
		reseller: "Перепродаю товары",
	},
	category: {
		womenClothing: "Женская одежда",
		menClothing: "Мужская одежда",
		shoes: "Обувь",
		kids: "Для детей",
		cosmetics: "Косметика и уход",
		health: "Здоровье",
		home: "Для дома и интерьера",
		accessories: "Аксессуары",
		electronics: "Электроника и техника",
		pets: "Для питомцев",
	},
	channel: {
		ownStore: "Собственный интернет-магазин",
		marketplaces: "Маркетплейсы (WB, OZON)",
		social: "Социальные сети",
		classifieds: "Доски объявлений (Авито)",
		offlineStores: "Офлайн-магазины",
		wholesale: "Оптовые продажи",
		partners: "Продажа через партнёрские сети",
		none: "Пока не использует каналы продаж",
		instagram: "Instagram",
		own_website: "Собственный сайт",
		markets: "Ярмарки и маркеты",
	},
	contact: {
		phone: "Телефон",
		telegram: "Telegram",
		whatsapp: "WhatsApp",
		email: "Почта",
	},
};

export function surveyLabel(kind: keyof typeof SURVEY_LABELS, code: string | null | undefined): string {
	const value = code?.trim() ?? "";
	return SURVEY_LABELS[kind][value] ?? value;
}

export function surveyList(kind: keyof typeof SURVEY_LABELS, codes: string[] | null | undefined): string {
	return codes && codes.length ? codes.map((code) => surveyLabel(kind, code)).join(", ") : "—";
}
